import React from 'react';
import {
  AgentChatMessageDto,
  AgentSessionDto,
  createAgentSession,
  getAgentMessages,
  sendAgentMessage,
  useApiClient,
} from '../api/client';
import { useI18n } from '../i18n/I18nProvider';
import { useToast } from '../toast/ToastProvider';

function formatTime(value: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export const AgentAssistantPage: React.FC = () => {
  const { request } = useApiClient();
  const { t } = useI18n();
  const { showError, showInfo } = useToast();
  const [session, setSession] = React.useState<AgentSessionDto | null>(null);
  const [messages, setMessages] = React.useState<AgentChatMessageDto[]>([]);
  const [draft, setDraft] = React.useState('');
  const [initializing, setInitializing] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const endRef = React.useRef<HTMLDivElement | null>(null);
  const starterPrompts = React.useMemo(
    () => [
      t('assistant.prompt.findRide'),
      t('assistant.prompt.showBookings'),
      t('assistant.prompt.bookRide'),
    ],
    [t]
  );

  React.useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        setInitializing(true);
        const created = await createAgentSession(request, { channel: 'CHAT' });
        if (cancelled) return;
        setSession(created);
        const existingMessages = await getAgentMessages(request, created.id);
        if (cancelled) return;
        setMessages(existingMessages);
      } catch (err) {
        if (cancelled) return;
        const message = (err as Error)?.message || t('assistant.error.initialize');
        setError(message);
        showError(message);
      } finally {
        if (!cancelled) setInitializing(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [request, showError, t]);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  async function submitMessage(prefilled?: string) {
    const content = (prefilled ?? draft).trim();
    if (!content || !session || sending) return;

    const optimisticMessage: AgentChatMessageDto = {
      id: `temp-${Date.now()}`,
      role: 'USER',
      content,
      createdAt: new Date().toISOString(),
    };

    setMessages((current) => [...current, optimisticMessage]);
    setDraft('');
    setSending(true);
    setError(null);

    try {
      const response = await sendAgentMessage(request, session.id, content);
      setMessages(response.messages);
    } catch (err) {
      const message = (err as Error)?.message || t('assistant.error.send');
      setError(message);
      showError(message);
      setMessages((current) => current.filter((item) => item.id !== optimisticMessage.id));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-[70vh] bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.18),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-8">
      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="rounded-[28px] border border-white/60 bg-slate-950 p-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-200">{t('assistant.brand')}</div>
          <h1 className="mt-3 text-3xl font-black tracking-tight">{t('assistant.title')}</h1>
          <p className="mt-3 text-sm leading-6 text-slate-300">
            {t('assistant.description')}
          </p>

          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('assistant.session')}</div>
            <div className="mt-2 text-sm text-slate-100">
              {session ? t('assistant.session.active', { id: session.id.slice(0, 8) }) : t('assistant.session.creating')}
            </div>
            <div className="mt-1 text-xs text-slate-400">{t('assistant.session.description')}</div>
          </div>

          <div className="mt-6">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('assistant.suggestedPrompts')}</div>
            <div className="mt-3 grid gap-3">
              {starterPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => submitMessage(prompt)}
                  disabled={!session || sending || initializing}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-left text-sm leading-5 text-slate-100 transition hover:border-amber-300/60 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={() => showInfo(t('assistant.confirmationHint'))}
            className="mt-6 rounded-full bg-amber-300 px-4 py-2 text-sm font-black text-slate-950 transition hover:brightness-95"
          >
            {t('assistant.confirmationHelp')}
          </button>
        </aside>

        <section className="overflow-hidden rounded-[32px] border border-slate-200/70 bg-white/85 shadow-[0_24px_80px_rgba(148,163,184,0.22)] backdrop-blur">
          <div className="border-b border-slate-200/80 px-6 py-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{t('assistant.channel')}</div>
                <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">{t('assistant.chatTitle')}</h2>
              </div>
              <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                {initializing ? t('assistant.status.initializing') : sending ? t('assistant.status.thinking') : t('assistant.status.ready')}
              </div>
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              {t('assistant.chatDescription')}
            </p>
          </div>

          <div className="flex h-[72vh] flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
              {initializing && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  {t('assistant.initializingSession')}
                </div>
              )}

              {!initializing && messages.length === 0 && (
                <div className="rounded-[28px] border border-dashed border-slate-300 bg-slate-50/80 px-6 py-10 text-center">
                  <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">{t('assistant.empty.eyebrow')}</div>
                  <div className="mt-3 text-2xl font-black tracking-tight text-slate-900">{t('assistant.empty.title')}</div>
                  <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500">
                    {t('assistant.empty.description')}
                  </p>
                </div>
              )}

              {messages.map((message) => {
                const isUser = message.role === 'USER';
                return (
                  <div key={message.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-[24px] px-5 py-4 shadow-sm ${
                        isUser
                          ? 'bg-slate-950 text-white'
                          : 'border border-slate-200 bg-[linear-gradient(180deg,_#ffffff_0%,_#f8fafc_100%)] text-slate-900'
                      }`}
                    >
                      <div className="whitespace-pre-wrap text-sm leading-6">{message.content}</div>
                      <div className={`mt-2 text-[11px] font-semibold uppercase tracking-[0.16em] ${isUser ? 'text-slate-300' : 'text-slate-400'}`}>
                        {isUser ? t('assistant.message.you') : t('assistant.brand')} · {formatTime(message.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div className="flex justify-start">
                  <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500 shadow-sm">
                    {t('assistant.typing')}
                  </div>
                </div>
              )}

              <div ref={endRef} />
            </div>

            <div className="border-t border-slate-200/80 bg-white/90 px-6 py-5">
              {error && (
                <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void submitMessage();
                }}
                className="flex flex-col gap-3"
              >
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  rows={3}
                  placeholder={t('assistant.inputPlaceholder')}
                  disabled={!session || initializing || sending}
                  className="w-full rounded-[24px] border border-slate-300 bg-white px-4 py-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-xs leading-5 text-slate-500">
                    {t('assistant.footerHint')}
                  </div>
                  <button
                    type="submit"
                    disabled={!draft.trim() || !session || initializing || sending}
                    className="rounded-full bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  >
                    {sending ? t('assistant.action.sending') : t('assistant.action.send')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
